"use client";

import React, { useState } from 'react';

const SimaxMethodologySection = () => {
  const [activePhase, setActivePhase] = useState(1);

  const phases = [
    {
      number: 1,
      title: "Discover",
      duration: "2-6 weeks",
      whatWeDo: "Deep dive into your current data landscape, pain points, and business objectives through stakeholder interviews, data source reviews, current reporting analysis, KPI gap identification, technology assessment, roadmap planning, and ROI projection.",
      youReceive: [
        "Data source inventory",
        "Pain point analysis",
        "EDW roadmap",
        "ROI projection and business case"
      ]
    },
    {
      number: 2,
      title: "Architect",
      duration: "2-4 weeks",
      whatWeDo: "Design data models, Select technology stack, Plan security framework, Define governance rules",
      youReceive: [
        "Technical Blueprint",
        "Security Architecture",
        "Data Governance Policy",
        "Architecture Sign-Off"
      ]
    },
    {
      number: 3,
      title: "Build",
      duration: "8-16 weeks",
      whatWeDo: "Develop ETL pipelines, Build data warehouse, Implement quality checks, Load historical data",
      youReceive: [
        "Production EDW",
        "Automated Data Flows",
        "Quality Assurance Reports",
        "Performance Benchmarks"
      ]
    },
    {
      number: 4,
      title: "Visualize",
      duration: "4-8 weeks",
      whatWeDo: "Build executive dashboards, Create operational reports, Enable self-service BI, Train your teams",
      youReceive: [
        "C-Suite Dashboards",
        "Departmental Reports",
        "User Training",
        "Documentation"
      ]
    },
    {
      number: 5,
      title: "Evolve",
      duration: "Ongoing",
      whatWeDo: "Monitor performance, Add new data sources, Optimize queries, Provide support",
      youReceive: [
        "24/7 Support",
        "Monthly Health Reports",
        "Continuous Optimization",
        "Dedicated Account Manager"
      ]
    }
  ];

  const currentPhase = phases?.find(p => p?.number === activePhase) || phases?.[0];

  return (
    <section className="bg-navy py-12 sm:py-16 md:py-24 min-h-0 flex items-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-left md:text-center mb-8 sm:mb-12 md:mb-16">
          <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] mb-3 sm:mb-4">
            OUR PROVEN PROCESS
          </p>
          <h2 className="font-serif text-white text-[26px] sm:text-[34px] md:text-[44px] lg:text-[48px] leading-[1.2] font-bold mb-4 sm:mb-6">
            The Simax 5-Phase Methodology
          </h2>
          <p className="text-white/80 text-[15px] sm:text-base md:text-xl leading-[1.7] max-w-3xl md:mx-auto">
            No surprises. No scope creep. Just transparent, phased execution.
          </p>
        </div>

        {/* Horizontal Timeline Navigation - scroll on mobile; connector lines only in gaps (touch circle edges) */}
        <div className="mb-8 sm:mb-12 md:mb-20 -mx-4 sm:mx-0">
          <div className="flex justify-start sm:justify-between gap-4 sm:gap-0 max-w-5xl mx-auto overflow-x-auto overflow-y-hidden pb-2 sm:pb-0 px-4 sm:px-0">
            {phases?.map((phase, index) => (
              <React.Fragment key={phase?.number}>
                <div className="flex flex-col items-center relative z-10 flex-shrink-0">
                  <button
                    onClick={() => setActivePhase(phase?.number)}
                    className={`w-[48px] h-[48px] sm:w-[56px] sm:h-[56px] md:w-[60px] md:h-[60px] rounded-full flex items-center justify-center text-[18px] sm:text-[22px] md:text-[24px] font-bold transition-all duration-300 cursor-pointer touch-manipulation ${
                      activePhase === phase?.number
                        ? 'bg-gold text-navy shadow-lg shadow-gold/40'
                        : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white/80 active:bg-white/20'
                    }`}
                  >
                    {phase?.number}
                  </button>
                  <p className={`mt-2 sm:mt-4 text-[13px] sm:text-[16px] font-medium transition-colors duration-300 whitespace-nowrap ${
                    activePhase === phase?.number ? 'text-gold' : 'text-white/50'
                  }`}>
                    {phase?.title}
                  </p>
                </div>
                {index < phases.length - 1 && (
                  <div
                    className="hidden sm:flex flex-1 min-w-[8px] md:min-w-[12px] items-center justify-center shrink-0 self-start h-[56px] md:h-[60px]"
                    aria-hidden
                  >
                    <div className="w-full max-w-full h-px bg-white/20" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Dynamic Phase Content */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10 lg:p-12 transition-all duration-500">
            {/* Phase Header */}
            <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8 md:mb-10">
              <div className="flex-shrink-0">
                <div className="w-[56px] h-[56px] sm:w-[64px] sm:h-[64px] md:w-[80px] md:h-[80px] rounded-full bg-gold flex items-center justify-center">
                  <span className="text-navy text-[24px] sm:text-[28px] md:text-[36px] font-bold">{currentPhase?.number}</span>
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-white text-[24px] sm:text-[30px] md:text-[38px] lg:text-[42px] font-normal mb-1 sm:mb-2 font-serif">
                  {currentPhase?.title}
                </h3>
                <p className="text-gold text-[14px] sm:text-[16px] font-medium">
                  {currentPhase?.duration}
                </p>
              </div>
            </div>

            {/* Two Column Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
              {/* Left Column - What We Do */}
              <div>
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                  <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="text-white text-[14px] font-bold tracking-[0.15em] uppercase">
                    WHAT WE DO
                  </h4>
                </div>
                <p className="text-white/80 text-[14px] sm:text-[16px] leading-[1.8]">
                  {currentPhase?.whatWeDo}
                </p>
              </div>

              {/* Right Column - You Receive */}
              <div>
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                  <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h4 className="text-white text-[14px] font-bold tracking-[0.15em] uppercase">
                    YOU RECEIVE
                  </h4>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {currentPhase?.youReceive?.map((item, i) => (
                    <li key={i} className="text-white/80 text-[14px] sm:text-[16px] leading-[1.8] flex items-start">
                      <svg className="w-5 h-5 text-gold mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Additional Info Section */}
            <div className="mt-6 sm:mt-8 md:mt-10 pt-6 sm:pt-8 border-t border-white/10">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-white/60 text-[14px] leading-[1.7] italic">
                  Every phase has defined deliverables. You approve before we proceed. No black boxes. No uncertainty.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-8 sm:mt-12 text-left md:text-center">
          <p className="text-white/90 text-[15px] sm:text-base md:text-lg leading-[1.7] max-w-4xl md:mx-auto">
            <span className="text-gold font-semibold">Our Goal:</span> A structured, predictable, and transparent implementation journey 
            that transforms your data from chaos to clarity.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SimaxMethodologySection;